import {
  type NodeProps,
  type Edge as ReactFlowEdge,
  type Node as ReactFlowNode,
} from '@xyflow/react'
import { WorkflowContainerFragment } from 'generated/resources/graphql'

import {
  BackendActionNodeData,
  BackendSwitchComparison,
  BackendSwitchNodeData,
} from 'modules/workflows/lib/backend-types'

export type ActionGroupType = 'task' | 'approvals'

export type NodeUIType = 'trigger' | 'condition' | 'conditionIf' | 'conditionElse' | ActionGroupType

export interface BasicNodeUIData extends Record<string, unknown> {
  step?: number
  title: string
  description: string
  isValid: boolean
  suppressInitialValidation?: boolean
  nodeAttachment?: React.ReactNode
  workflowNodeFiles?: WorkflowContainerFragment['workflowNodeFiles']
}

type BasicNodeUI = ReactFlowNode<BasicNodeUIData, NodeUIType>

export type NodeUICondition = BasicNodeUI & {
  type: 'condition'
  data: BasicNodeUIData & Omit<BackendSwitchNodeData, '_type'>
}

export type NodeUITrigger = BasicNodeUI & { type: 'trigger'; data: BasicNodeUIData }

export type NodeUITask = BasicNodeUI & {
  type: 'task'
  data: BasicNodeUIData & Omit<BackendActionNodeData, '_type'>
}
export type NodeUIApproval = BasicNodeUI & {
  type: 'approvals'
  data: BasicNodeUIData & Omit<BackendActionNodeData, '_type'>
}

export type NodeUIConditionIf = BasicNodeUI & {
  type: 'conditionIf'
  data: BasicNodeUIData & {
    comparison: BackendSwitchComparison
  }
}
export type NodeUIConditionElse = BasicNodeUI & {
  type: 'conditionElse'
  data: BasicNodeUIData
}

export type NodeUIAction = NodeUITask | NodeUIApproval

export type NodeUI =
  | NodeUITrigger
  | NodeUITask
  | NodeUIApproval
  | NodeUICondition
  | NodeUIConditionIf
  | NodeUIConditionElse

export type EdgeUI = ReactFlowEdge

// type guards
// NodeProps type guards (for React components)
export const isNodePropsCondition = (
  props: NodeProps<NodeUI>
): props is NodeProps<NodeUICondition> => {
  return props.type === 'condition'
}
export const isNodePropsConditionIf = (
  props: NodeProps<NodeUI>
): props is NodeProps<NodeUIConditionIf> => {
  return props.type === 'conditionIf'
}

export const isNodeUICondition = (node: NodeUI | undefined): node is NodeUICondition => {
  return !!node && node.type === 'condition'
}

export const isNodeUIConditionIfOrElse = (
  node: NodeUI | undefined
): node is NodeUIConditionIf | NodeUIConditionElse => {
  return !!node && (node.type === 'conditionIf' || node.type === 'conditionElse')
}
export const isValidActionGroup = (value: string): value is ActionGroupType => {
  return value === 'task' || value === 'approvals'
}
